import React from 'react';
import useLocales from 'hooks/useLocales';
import { ICandidate } from 'core/interface/candidate';
import Image from 'components/image/Image';
import Pagination from 'components/shared/Pagination/Index';
import { RequestsService } from 'services/requests/request';
import { MatchProfileService } from 'services/matchProfile/matchProfile';
import { useNavigate, useParams } from 'react-router';
import { CircularProgress } from '@mui/material';
import NoData from 'components/shared/NoData/Index';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';

import './index.scss';
import { getDateFormatter } from 'helpers/dateConverter';
import { CANDIDATE_ROUTES } from 'core/constants/candidateRoutes';
import { DynamicActionButton } from 'components/newButtons/DynamicActionButton';
import StatusIcon from 'components/newButtons/StatusIcon';
import OrderBySelect from 'components/shared/OrderBySelect/Index';
import { ScoreOrdering } from 'core/constants/ordering';

const MatchResults = ({
  handleModal,
  getMatchCandidates,
  matchProfileCandidate,
  ordering,
  onOrderingChange,
  isFindCandidateRoute
}: any): JSX.Element => {
  const { t } = useLocales();
  const navigate = useNavigate();
  const { id } = useParams();

  const getUserProfile = (id: string) => {
    navigate(CANDIDATE_ROUTES.profile(id));
  };
  const { enqueueSnackbar } = useSnackbar();
  const [candidates, setCandidates] = useState(
    matchProfileCandidate?.results || []
  );

  const getPersonName = (
    person?: { name?: string; first_name?: string; last_name?: string } | null
  ): string | null => {
    if (!person) return null;
    const full = [person.first_name, person.last_name].filter(Boolean).join(' ').trim();
    return person.name?.trim() || full || null;
  };

  const sendRequest = async (candidateId: number, originId: number) => {
    try {
      const response = await RequestsService.sendRequest(
        isFindCandidateRoute
          ? { origin_profile: originId, candidate: candidateId }
          : { origin_job: originId, candidate: candidateId },
        { limit: 10, offset: 0 }
      );
      if (response && response.id) {
        setCandidates((prevCandidates: any[]) =>
          prevCandidates.map((candidate: ICandidate) =>
            candidate.id === response?.candidate?.id
              ? {
                  ...candidate,
                  profile_request: {
                    id: response.id,
                    status: response.status ?? 1,
                    created_at: response.created_at,
                    requested_by: response.requested_by ?? null
                  }
                }
              : candidate
          )
        );
        enqueueSnackbar('Request has been sent', { variant: 'success' });
      }
    } catch (error) {
      enqueueSnackbar('Failed to send request', { variant: 'error' });
    }
  };

  const withdrawRequest = async (requestId: number) => {
    try {
      const response = await RequestsService.changeRequest({ status: 4 }, requestId);
      if (response) {
        setCandidates((prevCandidates: any[]) =>
          prevCandidates.map((candidate: ICandidate) =>
            candidate.profile_request?.id === requestId
              ? { ...candidate, profile_request: null }
              : candidate
          )
        );
        if (id) {
          await RequestsService.getRequest(+id, undefined, 10, 0);
          await RequestsService.getRequest(undefined, undefined, 6, 0);
        }
      }
    } catch (error) {
      enqueueSnackbar('Failed to withdraw request', { variant: 'error' });
    }
  };

  const selectCandidate = async (candidateId: number) => {
    if (!id) return;
    try {
      const response = await MatchProfileService.selectCandidate(+id, candidateId);
      if (!response?.id) {
        enqueueSnackbar('Failed to select candidate', { variant: 'error' });
        return;
      }

      setCandidates((prevCandidates: ICandidate[]) =>
        prevCandidates.map((candidate: ICandidate) =>
          String(candidate.id) === String(candidateId)
            ? {
                ...candidate,
                selected: {
                  id: response.id,
                  created_at: response.created_at,
                  selected_by: response.selected_by ?? null
                }
              }
            : candidate
        )
      );
      enqueueSnackbar('Candidate selected', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to select candidate', { variant: 'error' });
    }
  };

  useEffect(() => {
    setCandidates([]);
  }, [id]);

  useEffect(() => {
    setCandidates(matchProfileCandidate?.results || []);
  }, [matchProfileCandidate]);

  const renderHistoryColumn = (candidate: ICandidate) => {
    const lines: string[] = [];
    const dateFormat = 'MMM DD YYYY';

    const pr = candidate.profile_request;
    if (pr) {
      const byName = getPersonName(pr.requested_by);
      const date = pr.created_at
        ? getDateFormatter(pr.created_at, dateFormat)
        : '';
      lines.push(
        byName
          ? `${t('Profile request sent on')} ${date} ${t('by')} ${byName}`
          : `${t('Profile request sent on')} ${date}`
      );
    }

    const sel = candidate.selected;
    if (isFindCandidateRoute && sel) {
      const byName = getPersonName(sel.selected_by);
      const date = sel.created_at
        ? getDateFormatter(sel.created_at, dateFormat)
        : '';
      lines.push(
        byName
          ? `${t('Selected on')} ${date} ${t('by')} ${byName}`
          : `${t('Selected on')} ${date}`
      );
    }

    const application = candidate.application;
    if (!isFindCandidateRoute && application) {
      const date = application.created_at
        ? getDateFormatter(application.created_at, dateFormat)
        : '';
      lines.push(`${t('Applicant since')} ${date}`);
    }

    return (
      <div className="history-column">
        {lines.map((line, i) => (
          <span key={i} className="history-column__line">{line}</span>
        ))}
      </div>
    );
  };

  const renderRequestButton = (candidate: ICandidate) => {
    const profileRequest = candidate.profile_request;

    if (profileRequest) {
      if (profileRequest.status === 1) {
        return (
          <DynamicActionButton
            label={t('Withdraw Request')}
            backgroundColor="red"
            onClick={() => withdrawRequest(profileRequest.id)}
          />
        );
      }
      if (profileRequest.status === 3 || profileRequest.status === 4) {
        return <StatusIcon status="rejected" />;
      }
      return null;
    }

    if (candidate.can_view_profile) {
      return null;
    }

    return (
      <DynamicActionButton
        label={t('Send request')}
        backgroundColor="#0fb398"
        onClick={() => sendRequest(+candidate.id, id ? +id : -1)}
      />
    );
  };

  const renderSelectButton = (candidate: ICandidate) => {
    if (!isFindCandidateRoute) return null;
    if (!candidate.can_view_profile || candidate.selected) return null;
    return (
      <DynamicActionButton
        label={t('Select')}
        backgroundColor="#0fb398"
        onClick={() => selectCandidate(+candidate.id)}
      />
    );
  };

  return (
    <>
      <div className="result-content">
        <div className="results-container">
          <div className="results-header">
            <p className="title">{t('Match Results')}</p>
            {onOrderingChange && (
              <OrderBySelect
                value={ordering as ScoreOrdering}
                onChange={onOrderingChange}
              />
            )}
          </div>
          <div>
            {matchProfileCandidate === null ? (
              <div className="loadingContainer">
                <CircularProgress className="loading" />
              </div>
            ) : matchProfileCandidate?.results?.length === 0 ? (
              <NoData />
            ) : (
              candidates?.map((candidate: ICandidate) => (
                <div key={candidate.id} className="candidate">
                  <div className="profile-info">
                    <Image
                      className="profile-image"
                      url={candidate.avatar}
                      name="profile-view.svg"
                      width={75}
                      height={75}
                      objectFit="cover"
                    />

                    <div className="nameContainer">
                      <p className="name">{candidate.name}</p>
                      {candidate.bio && <span className="bio">{candidate.bio}</span>}
                    </div>
                  </div>
                  <div className="score">
                    <p className="score-text">
                      {candidate?.score}% {t('Talent Match')}
                    </p>
                    <p className="score-text">
                      {candidate?.culture_score}% {t('Culture Match')}
                    </p>
                  </div>
                  {renderHistoryColumn(candidate)}
                  <div className="buttons">
                    <DynamicActionButton
                      label={t('View teaser')}
                      backgroundColor="#BEA0E5"
                      onClick={() => handleModal(true, candidate?.id)}
                    />

                    {candidate.can_view_profile && (
                      <DynamicActionButton
                        label={t('View Profile')}
                        backgroundColor="#A0D5E5"
                        onClick={() => getUserProfile(candidate?.id)}
                      />
                    )}
                    {renderRequestButton(candidate)}
                    {renderSelectButton(candidate)}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        <Pagination
          key={ordering}
          hasNext={
            matchProfileCandidate?.next ? matchProfileCandidate.next : null
          }
          itemsPerPage={10}
          dataLength={matchProfileCandidate?.count ?? 1}
          onNext={(e) => getMatchCandidates(10, e.prevPage)}
          onPrev={(e) =>
            getMatchCandidates(10, e.prevPage !== 0 ? e.prevPage : 0)
          }
        />
      </div>
    </>
  );
};

export default MatchResults;
